import { useMutation, UseQueryResult } from '@tanstack/react-query'
import { queryClient, QueryOptions, UseMutationResult, useResource, useResources } from '.'
import {
  create,
  fetch,
  findDistinctTypes,
  get,
  SpecialPeriod,
  SpecialPeriodCreationPayload,
  SpecialPeriodListParams,
  SpecialPeriodUpdatePayload,
  update,
} from '../api/special-periods'

export const specialPeriodKeys = {
  create: ['special-periods', 'create'] as const,
  update: ['special-periods', 'update'] as const,
  delete: ['special-periods', 'delete'] as const,
}

/**
 * Query hook for fetching a single special period
 */
export const useSpecialPeriodQuery = (
  id?: string,
  options?: QueryOptions<SpecialPeriod>,
): UseQueryResult<SpecialPeriod, any> =>
  useResource<SpecialPeriod>('special-periods', get, id, undefined, options)

/**
 * Query hook for fetching multiple special periods with optional filters
 */
export const useSpecialPeriodsQuery = (
  filters?: SpecialPeriodListParams,
  options?: QueryOptions<SpecialPeriod[]>,
): UseQueryResult<SpecialPeriod[], any> =>
  useResources<SpecialPeriod[], SpecialPeriodListParams>(
    'special-periods',
    fetch,
    filters || {},
    options,
  )

/**
 * Mutation success handler - invalidates special periods queries
 */
const onMutationSuccess = () => {
  queryClient.invalidateQueries(['special-periods'])
}

/**
 * Mutation hook for creating a special period
 */
export const useCreateMutation = (): UseMutationResult<
  SpecialPeriod,
  SpecialPeriodCreationPayload
> => useMutation(create, { mutationKey: specialPeriodKeys.create, onSuccess: onMutationSuccess })

/**
 * Mutation hook for updating a special period
 */
export const useUpdateMutation = (): UseMutationResult<
  SpecialPeriod,
  SpecialPeriodUpdatePayload
> =>
  useMutation(update, {
    mutationKey: specialPeriodKeys.update,
    onSuccess: onMutationSuccess,
  })

/**
 * Query hook for fetching distinct types of special periods for a given employer
 */
export const useSpecialPeriodTypesQuery = (
  employerId?: string,
  options?: QueryOptions<string[]>,
): UseQueryResult<string[], any> =>
  useResource<string[]>('special-periods-types', findDistinctTypes, employerId, undefined, options)
